/**
 * The route-level states: booting, unreachable, not found, forbidden, a page
 * that crashed, and the moment a page's chunk is still downloading.
 *
 * Drawn with the design system's state primitives, so they look like every
 * page's own empty and error states rather than like a different product.
 */

import { ArrowClockwise, Compass, LockKey } from '@phosphor-icons/react';
import { createContext, useContext, type ReactNode } from 'react';

import { messageOf } from '@/lib/api';
import { platformPermissionLabel } from '@/lib/format';
import { t } from '@/lib/i18n';
import { paths } from '@/lib/nav';
import type { PlatformPermission } from '@/lib/platform';
import { Button, ButtonLink, EmptyState, ErrorState, Loading, Page, Skeleton, SkeletonText, Stack } from '@/ui';

/**
 * True under a frame that has already drawn the `Page` — the tenant workspace,
 * whose tabs are routes of their own. A tab's 403, 404, crash or loading state
 * then renders in the tab panel instead of nesting a second page in the first.
 */
const InsidePage = createContext(false);

export function InsidePageProvider({ children }: { children: ReactNode }) {
  return <InsidePage.Provider value={true}>{children}</InsidePage.Provider>;
}

function Frame({ children }: { children: ReactNode }) {
  return useContext(InsidePage) ? <>{children}</> : <Page>{children}</Page>;
}

/** Before `/me` has answered. No shell yet: we do not know whose shell to draw. */
export function Booting() {
  return (
    <div className="state" aria-busy="true">
      <Loading label={t('frame.booting')} />
    </div>
  );
}

/** `/me` failed for a reason other than auth. Not a sign-out: the session may be fine. */
export function Unreachable({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <Page>
      <ErrorState title={t('frame.bootFailed')} message={messageOf(error)} onRetry={onRetry} />
    </Page>
  );
}

export function NotFound() {
  return (
    <Frame>
      <EmptyState
        icon={Compass}
        title={t('frame.notFoundTitle')}
        body={t('frame.notFoundBody')}
        action={<ButtonLink to={paths.dashboard}>{t('frame.notFoundHome')}</ButtonLink>}
      />
    </Frame>
  );
}

export function Forbidden({ permission }: { permission: PlatformPermission }) {
  return (
    <Frame>
      <EmptyState
        icon={LockKey}
        title={t('frame.forbiddenTitle')}
        body={t('frame.forbiddenBody', { permission: platformPermissionLabel(permission) })}
        action={<ButtonLink to={paths.dashboard}>{t('frame.notFoundHome')}</ButtonLink>}
      />
    </Frame>
  );
}

/** A page threw while rendering. The toolbar is still there; so is "try again". */
export function Crashed({ onRetry }: { onRetry: () => void }) {
  return (
    <Frame>
      <ErrorState title={t('frame.errorTitle')} message={t('error.boundary')} onRetry={onRetry} />
    </Frame>
  );
}

/** A chunk failed to load after a deploy. Retrying cannot help; a reload can. */
export function StaleBuild() {
  return (
    <Frame>
      <EmptyState
        icon={ArrowClockwise}
        title={t('frame.chunkTitle')}
        body={t('error.chunk')}
        action={
          <Button variant="primary" icon={ArrowClockwise} onClick={() => window.location.reload()}>
            {t('common.reload')}
          </Button>
        }
      />
    </Frame>
  );
}

/**
 * While a page's chunk downloads: the generic shape of a page (a title, a
 * line, a block). The page's own skeleton takes over the moment it mounts.
 */
export function ChunkLoading() {
  return (
    <Frame>
      <Stack gap="6">
        <span role="status" aria-busy="true" className="sr-only">
          {t('common.loading')}
        </span>
        <Stack gap="2">
          <Skeleton width="16rem" height="2rem" />
          <Skeleton width="28rem" />
        </Stack>
        <Skeleton shape="block" height="12rem" />
        <SkeletonText lines={4} />
      </Stack>
    </Frame>
  );
}
